import React, { useEffect, useRef } from 'react';

export interface SectionTabItem {
  key: string;
  label: string;
}

interface SectionTabsProps {
  tabs: SectionTabItem[];
  active: string;
  onChange: (key: string) => void;
  /** Mặc định dính đầu dưới header khi cuộn trang; trong hộp thoại thì tắt. */
  sticky?: boolean;
}

/**
 * Thanh tab dùng chung: hàng nút dạng viên thuốc, cuộn ngang trên điện thoại (không xuống
 * dòng, ẩn thanh cuộn), dính đầu dưới header khi cuộn trang. Dùng cho khu Cài đặt Donate
 * và tab Donate trang cá nhân — mỗi nơi tự lưu tab đang chọn lên URL theo query param riêng.
 */
const SectionTabs: React.FC<SectionTabsProps> = ({ tabs, active, onChange, sticky = true }) => {
  const listRef = useRef<HTMLDivElement>(null);

  // Điện thoại: tab đang chọn có thể nằm khuất bên phải (vd mở thẳng ?muc=nhac-ai) — cuộn
  // ngang riêng thanh tab tới giữa tab đó. Không dùng scrollIntoView để khỏi kéo cả trang.
  useEffect(() => {
    const list = listRef.current;
    const btn = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !btn || list.scrollWidth <= list.clientWidth) return;
    list.scrollTo({ left: btn.offsetLeft - list.offsetLeft - (list.clientWidth - btn.offsetWidth) / 2, behavior: 'smooth' });
  }, [active, tabs.length]);

  return (
    <div className={`${sticky ? 'sticky top-[--m-top] z-10 ' : ''}-mx-1 bg-white px-1 py-2 dark:bg-black`}>
      <div
        ref={listRef}
        role="tablist"
        className="flex items-center gap-2 overflow-x-auto whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((t) => {
          const selected = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onChange(t.key)}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                selected
                  ? 'bg-blue-600 text-white'
                  : 'ui-surface-2 ui-text hover:bg-blue-50 dark:hover:bg-slate-600'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SectionTabs;
